import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ThemeToggle } from "@/components/ThemeToggle";
import { requestPasswordReset } from "@/lib/auth";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
    } catch {
      // Deliberately swallowed: the backend answer is uniform either way and
      // the confirmation copy below must not depend on account existence.
    }
    setSent(true);
    setSubmitting(false);
  }

  return (
    <div className="canvas-ambient relative flex h-screen items-center justify-center bg-canvas">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-sm overflow-hidden rounded-xl border border-line-subtle bg-surface p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] space-y-6">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric-blue/60 to-transparent"
        />

        <div className="text-center space-y-2">
          <img src="/phantm-logo.png" alt="Phantm" className="mx-auto h-12 w-12 object-contain" />
          <h1 className="pt-1 text-xl font-semibold tracking-tight text-fg">
            Reset your password
          </h1>
        </div>

        {sent ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-fg">
              If an account exists for that email, we've sent a reset link. It
              expires in about an hour.
            </p>
            <Link to="/login" className="text-sm text-electric-blue hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-center text-sm text-fg-muted">
              Enter your account email and we'll send you a reset link.
            </p>
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
                autoComplete="email"
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
              />
            </div>
            <button
              type="submit"
              disabled={!email.trim() || submitting}
              className="w-full rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-all hover:bg-electric-blue/90 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-center">
              <Link to="/login" className="text-xs text-fg-muted hover:text-fg">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
